import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { Button } from './ui/button';

const CostCalculatorWizard = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({
    burialType: null,
    graveType: null,
    ceremony: null,
    additional: null,
    details: []
  });
  const [showResult, setShowResult] = useState(false);

  const steps = [
    {
      id: 'burialType',
      title: 'Bestattungsart',
      question: 'Welche Bestattungsart wird gewünscht?',
      subtitle: 'Die Wahl der Bestattungsart hat großen Einfluss auf die Gesamtkosten.',
      type: 'single',
      options: [
        { id: 'erd', title: 'Erdbestattung', description: 'Klassische Beisetzung im Sarg auf dem Friedhof', price: 3500 },
        { id: 'feuer', title: 'Feuerbestattung', description: 'Einäscherung mit anschließender Urnenbeisetzung', price: 2500 },
        { id: 'see', title: 'Seebestattung', description: 'Beisetzung der Urne auf See', price: 3000 },
        { id: 'wald', title: 'Waldbestattung', description: 'Naturnahe Urnenbeisetzung im Bestattungswald', price: 2800 },
        { id: 'anonym', title: 'Anonyme Bestattung', description: 'Einfache Beisetzung ohne Grabkennzeichnung', price: 1000 }
      ]
    },
    {
      id: 'graveType',
      title: 'Grabstelle',
      question: 'Welche Grabstelle wird gewünscht?',
      subtitle: 'Die Art der Grabstelle beeinflusst die Friedhofsgebühren und laufenden Kosten.',
      type: 'single',
      options: [
        { id: 'anonym', title: 'Anonymes Grab', description: 'Keine namentliche Kennzeichnung', price: 900 },
        { id: 'reihe', title: 'Reihengrab', description: 'Einzelgrab mit fester Ruhezeit', price: 1500 },
        { id: 'wahl', title: 'Wahlgrab', description: 'Grabstelle frei wählbar, verlängerbar', price: 2400 },
        { id: 'gemein', title: 'Gemeinschaftsgrab', description: 'Grabstelle in einer Gemeinschaftsanlage', price: 2000 },
        { id: 'baum', title: 'Baumbestattung', description: 'Urnenbeisetzung am Fuß eines Baumes', price: 1900 }
      ]
    },
    {
      id: 'ceremony',
      title: 'Trauerfeier',
      question: 'Soll eine Trauerfeier stattfinden?',
      subtitle: 'Eine Trauerfeier bietet Raum für den gemeinsamen Abschied und das Gedenken.',
      type: 'single',
      options: [
        { id: 'gross', title: 'Große Trauerfeier', description: 'Mit Trauerredner, Blumenschmuck, Musik und Leichenschmaus', price: 2500 },
        { id: 'klein', title: 'Kleine Trauerfeier', description: 'Im engsten Familienkreis mit einfacher Gestaltung', price: 1200 },
        { id: 'still', title: 'Stille Beisetzung', description: 'Ohne Feier, nur die Beisetzung selbst', price: 400 },
        { id: 'keine', title: 'Keine Trauerfeier', description: 'Verzicht auf jegliche Feierlichkeit', price: 0 }
      ]
    },
    {
      id: 'additional',
      title: 'Zusatzleistungen',
      question: 'Welche zusätzlichen Leistungen sind gewünscht?',
      subtitle: 'Diese Posten können die Gesamtkosten einer Bestattung deutlich beeinflussen.',
      type: 'single',
      options: [
        { id: 'grabstein', title: 'Grabstein & Gravur', description: 'Individueller Grabstein mit Beschriftung', price: 3000 },
        { id: 'pflege', title: 'Dauergrabpflege', description: 'Professionelle Pflege der Grabstelle über 20 Jahre', price: 7500 },
        { id: 'einfach', title: 'Einfacher Grabstein', description: 'Standard-Grabstein ohne individuelle Gestaltung', price: 1000 },
        { id: 'keine', title: 'Keine Zusatzleistungen', description: 'Nur die grundlegenden Bestattungskosten', price: 0 }
      ]
    },
    {
      id: 'details',
      title: 'Detailoptionen',
      question: 'Welche weiteren Details möchten Sie hinzufügen?',
      subtitle: 'Wählen Sie alle gewünschten Leistungen aus.',
      type: 'multi',
      options: [
        { id: 'blumen', title: 'Blumenschmuck', price: 300 },
        { id: 'sargschmuck', title: 'Sargschmuck', price: 200 },
        { id: 'redner', title: 'Trauerredner', price: 260 },
        { id: 'anzeige', title: 'Traueranzeige', price: 450 },
        { id: 'brief', title: 'Trauerbrief', price: 100 },
        { id: 'traeger', title: 'Sargträger', price: 200 },
        { id: 'kleidung', title: 'Sterbekleidung', price: 100 },
        { id: 'hygiene', title: 'Hygienische Versorgung', price: 150 },
        { id: 'schmaus', title: 'Leichenschmaus', price: 800 },
        { id: 'aufbahrung', title: 'Aufbahrung', price: 300 },
        { id: 'musik', title: 'Musikalische Begleitung', price: 275 }
      ]
    }
  ];

  const calculateTotal = () => {
    let total = 0;
    if (selections.burialType) total += selections.burialType.price;
    if (selections.graveType) total += selections.graveType.price;
    if (selections.ceremony) total += selections.ceremony.price;
    if (selections.additional) total += selections.additional.price;
    selections.details.forEach(detail => total += detail.price);
    return total;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleSelect = (option) => {
    const stepId = steps[currentStep].id;
    
    if (steps[currentStep].type === 'single') {
      setSelections(prev => ({ ...prev, [stepId]: option }));
    } else {
      setSelections(prev => {
        const current = prev[stepId] || [];
        const exists = current.find(item => item.id === option.id);
        
        if (exists) {
          return { ...prev, [stepId]: current.filter(item => item.id !== option.id) };
        } else {
          return { ...prev, [stepId]: [...current, option] };
        }
      });
    }
  };

  const isSelected = (option) => {
    const stepId = steps[currentStep].id;
    
    if (steps[currentStep].type === 'single') {
      return selections[stepId]?.id === option.id;
    } else {
      return (selections[stepId] || []).some(item => item.id === option.id);
    }
  };

  const canProceed = () => {
    const stepId = steps[currentStep].id;
    if (steps[currentStep].type === 'single') {
      return selections[stepId] !== null;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(0);
    setSelections({
      burialType: null,
      graveType: null,
      ceremony: null,
      additional: null,
      details: []
    });
    setShowResult(false);
  };

  const currentStepData = steps[currentStep];
  const progressPercentage = ((currentStep + 1) / steps.length) * 100;

  // Result Screen
  if (showResult) {
    return (
      <div className="wizard-overlay">
        <div className="wizard-modal result-modal">
          <div className="wizard-header-minimal">
            <h2 className="wizard-title-main">Ihre Kostenübersicht</h2>
            <button onClick={onClose} className="wizard-close-btn">
              <X size={20} />
            </button>
          </div>

          <div className="wizard-content-scroll">
            <div className="result-container">
              <div className="result-total-card">
                <div className="result-total-label">Geschätzte Gesamtkosten</div>
                <div className="result-total-amount">{formatPrice(calculateTotal())}</div>
                <p className="result-total-note">
                  Dies ist eine unverbindliche Schätzung. Die tatsächlichen Kosten können je nach Region und Anbieter variieren.
                </p>
              </div>

              <div className="result-section-divider">
                <span>Ihre Auswahl im Detail</span>
              </div>

              {[
                { key: 'burialType', label: 'Bestattungsart', data: selections.burialType },
                { key: 'graveType', label: 'Grabstelle', data: selections.graveType },
                { key: 'ceremony', label: 'Trauerfeier', data: selections.ceremony },
                { key: 'additional', label: 'Zusatzleistungen', data: selections.additional }
              ].map(({ key, label, data }) => data && (
                <div key={key} className="result-section">
                  <div className="result-section-label">{label}</div>
                  <div className="result-row">
                    <div className="result-text">
                      <div className="result-title">{data.title}</div>
                      {data.description && <div className="result-desc">{data.description}</div>}
                    </div>
                    <div className="result-price">{formatPrice(data.price)}</div>
                  </div>
                </div>
              ))}

              {selections.details.length > 0 && (
                <div className="result-section">
                  <div className="result-section-label">Weitere Details</div>
                  {selections.details.map(detail => (
                    <div key={detail.id} className="result-row compact">
                      <div className="result-text">
                        <div className="result-title">{detail.title}</div>
                      </div>
                      <div className="result-price">{formatPrice(detail.price)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="result-cta-group">
                <Button 
                  onClick={() => {
                    onClose();
                    setTimeout(() => {
                      document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                    }, 300);
                  }}
                  size="lg"
                  className="wizard-btn-primary"
                >
                  Angebot anfordern
                </Button>
                <Button 
                  onClick={handleReset}
                  size="lg"
                  variant="outline"
                  className="wizard-btn-secondary"
                >
                  Neue Berechnung
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Wizard Steps
  return (
    <div className="wizard-overlay">
      <div className="wizard-modal">
        <div className="wizard-header-bar">
          <div className="wizard-header-top">
            <div className="wizard-header-left">
              <h2 className="wizard-title-main">Bestattungskosten-Rechner</h2>
              <p className="wizard-step-info">Schritt {currentStep + 1} von {steps.length}</p>
            </div>
            <div className="wizard-header-right">
              <div className="wizard-total-display">
                <span className="wizard-total-label">Zwischensumme</span>
                <span className="wizard-total-value">{formatPrice(calculateTotal())}</span>
              </div>
              <button onClick={onClose} className="wizard-close-btn">
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="wizard-progress-track">
            <div className="wizard-progress-fill" style={{ width: `${progressPercentage}%` }}></div>
          </div>
        </div>

        <div className="wizard-content-scroll">
          <div className="wizard-step-header">
            <h3 className="wizard-question">{currentStepData.question}</h3>
            <p className="wizard-subtitle-text">{currentStepData.subtitle}</p>
          </div>

          <div className={`wizard-options-grid ${currentStepData.type === 'multi' ? 'multi-column' : ''}`}>
            {currentStepData.options.map((option) => (
              <div
                key={option.id}
                className={`wizard-option-card ${isSelected(option) ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                <div className="wizard-option-content">
                  <div className="wizard-option-text">
                    <div className="wizard-option-title">{option.title}</div>
                    {option.description && (
                      <div className="wizard-option-desc">{option.description}</div>
                    )}
                  </div>
                  <div className="wizard-option-price">+ {formatPrice(option.price)}</div>
                </div>
                {isSelected(option) && currentStepData.type === 'single' && (
                  <div className="wizard-check-indicator">
                    <Check size={18} />
                  </div>
                )}
                {currentStepData.type === 'multi' && (
                  <div className={`wizard-checkbox ${isSelected(option) ? 'checked' : ''}`}>
                    {isSelected(option) && <Check size={14} />}
                  </div>
                )}
              </div>
            ))}
          </div>

          {currentStepData.type === 'multi' && (
            <p className="wizard-multi-hint">Mehrfachauswahl möglich – wählen Sie alle gewünschten Leistungen aus.</p>
          )}
        </div>

        <div className="wizard-footer-nav">
          <Button
            onClick={handleBack}
            disabled={currentStep === 0}
            variant="outline"
            size="lg"
            className="wizard-btn-back"
          >
            <ArrowLeft size={18} />
            Zurück
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            size="lg"
            className="wizard-btn-next"
          >
            {currentStep === steps.length - 1 ? 'Ergebnis anzeigen' : 'Weiter'}
            <ArrowRight size={18} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CostCalculatorWizard;
